import { Card, Badge } from '@tremor/react';
import { formatCurrency } from '../../utils/formatters';
import type { ThreeStateNOI } from '../../types/calculations';

interface ThreeStateNOICardProps {
  data: ThreeStateNOI;
}

export function ThreeStateNOICard({ data }: ThreeStateNOICardProps) {
  const states = [
    {
      label: 'As-Is',
      description: 'Current rents & occupancy — what the property earns today with existing leases and vacancies.',
      color: 'gray' as const,
      noi: data.as_is_noi,
      gpi: data.as_is_gpi,
      egi: data.as_is_egi,
      opex: data.as_is_opex,
    },
    {
      label: 'Stabilized',
      description: 'Full occupancy at current rents — no renovation, just lease up vacant units at today\'s in-place rates.',
      color: 'blue' as const,
      noi: data.stabilized_noi,
      gpi: data.stabilized_gpi,
      egi: data.stabilized_egi,
      opex: data.stabilized_opex,
    },
    {
      label: 'Uplifted',
      description: 'Post-renovation at market rents — fully leased after CapEx improvements at target market-rate rents.',
      color: 'green' as const,
      noi: data.uplifted_noi,
      gpi: data.uplifted_gpi,
      egi: data.uplifted_egi,
      opex: data.uplifted_opex,
    },
  ];

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-900 dark:text-gray-100">
          Three-State NOI Analysis
        </h3>
        <Badge color="purple">Value-Add Underwriting</Badge>
      </div>

      <div className="flex gap-4 text-sm text-gray-400 mb-3">
        <span><strong>GPI</strong> = Gross Potential Income (all units at market rent, 0% vacancy)</span>
        <span><strong>EGI</strong> = Effective Gross Income (GPI − vacancy &amp; credit loss)</span>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {states.map((state) => (
          <div
            key={state.label}
            className={`p-4 rounded-lg border-2 ${
              state.color === 'green'
                ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                : state.color === 'blue'
                ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
                : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Badge color={state.color}>{state.label}</Badge>
            </div>
            <p className="text-sm text-gray-500 mb-3">{state.description}</p>

            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500" title="Gross Potential Income — all units at market rent, 0% vacancy">GPI</span>
                <span className="font-mono tabular-nums">{formatCurrency(state.gpi)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500" title="Effective Gross Income — GPI minus vacancy and credit loss">EGI</span>
                <span className="font-mono tabular-nums">{formatCurrency(state.egi)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">OpEx</span>
                <span className="font-mono tabular-nums text-red-600">-{formatCurrency(state.opex)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="font-semibold">NOI</span>
                <span className="font-mono tabular-nums font-bold">{formatCurrency(state.noi)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delta callouts */}
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm text-blue-700 dark:text-blue-300">
              Lease-Up Gain
            </span>
            <span className="font-mono tabular-nums font-semibold text-blue-700">
              {data.lease_up_gain >= 0 ? '+' : ''}{formatCurrency(data.lease_up_gain)}
            </span>
          </div>
          <p className="text-sm text-blue-500 mt-1">No CapEx required</p>
        </div>
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm text-green-700 dark:text-green-300">
              NOI Gain (Post-Reno)
            </span>
            <span className="font-mono tabular-nums font-semibold text-green-700">
              {data.renovation_gain >= 0 ? '+' : ''}{formatCurrency(data.renovation_gain)}
            </span>
          </div>
          <p className="text-sm text-green-500 mt-1">net of vacancy &amp; OpEx</p>
        </div>
      </div>
    </Card>
  );
}
